import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS } from "@/lib/data";
import { TIMELINE } from "@/pages/AboutPage";

// A compact, horizontally scrollable chronology of Grup Xaluca's
// milestones — surfaced on the homepage heritage section as a quick
// visual complement to the full narrative on /about.
//
// UX pattern mirrors the existing XalucaOriginalsCarousel:
//   • Mobile: native touch swipe on a scroll-snap rail (no arrows).
//   • Tablet / desktop: chevron buttons scroll one card at a time,
//     disable at the extremes of the rail.
//
// Content is a straight reuse of the `TIMELINE` array declared on the
// About page, exported so both surfaces stay in sync — a new milestone
// added there appears here automatically.

const pick = (obj, lang) =>
  typeof obj === "string" ? obj : obj?.[lang] || obj?.en || "";

const HeritageTimelineCarousel = ({ testIdPrefix = "heritage-timeline" }) => {
  const { lang } = useLanguage();
  const railRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const refreshArrows = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    setCanPrev(rail.scrollLeft > 8);
    setCanNext(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 8);
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    refreshArrows();
    rail.addEventListener("scroll", refreshArrows, { passive: true });
    window.addEventListener("resize", refreshArrows);
    return () => {
      rail.removeEventListener("scroll", refreshArrows);
      window.removeEventListener("resize", refreshArrows);
    };
  }, [refreshArrows]);

  const scrollByCards = (dir) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector("[data-timeline-card]");
    const step = card ? card.getBoundingClientRect().width + 20 : rail.clientWidth * 0.85;
    rail.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!TIMELINE?.length) return null;

  return (
    <div className="relative" data-testid={testIdPrefix}>
      {/* Chevron nav — tablet+ only. Positioned outside the rail so they
          never overlap card content. */}
      <button
        type="button"
        onClick={() => scrollByCards(-1)}
        aria-label="Previous milestones"
        disabled={!canPrev}
        className={`hidden md:flex absolute -left-2 lg:-left-4 top-[42%] -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full border shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] transition-all bg-[#FDFBF7] ${
          canPrev
            ? "border-[#2C2621]/15 hover:border-[#C16542] text-[#2C2621] hover:text-[#C16542]"
            : "border-[#2C2621]/8 text-[#2C2621]/25 cursor-not-allowed"
        }`}
        data-testid={`${testIdPrefix}-prev`}
      >
        <ChevronLeft className="w-5 h-5" strokeWidth={1.6} />
      </button>
      <button
        type="button"
        onClick={() => scrollByCards(1)}
        aria-label="Next milestones"
        disabled={!canNext}
        className={`hidden md:flex absolute -right-2 lg:-right-4 top-[42%] -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full border shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] transition-all bg-[#FDFBF7] ${
          canNext
            ? "border-[#2C2621]/15 hover:border-[#C16542] text-[#2C2621] hover:text-[#C16542]"
            : "border-[#2C2621]/8 text-[#2C2621]/25 cursor-not-allowed"
        }`}
        data-testid={`${testIdPrefix}-next`}
      >
        <ChevronRight className="w-5 h-5" strokeWidth={1.6} />
      </button>

      <ul
        ref={railRef}
        className="flex gap-4 md:gap-5 overflow-x-auto pb-4 -mb-4 snap-x snap-mandatory scroll-px-4 md:scroll-px-6 px-1 md:px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid={`${testIdPrefix}-rail`}
      >
        {TIMELINE.map((t, i) => {
          const hotelMatch = t.slug ? HOTELS.find((h) => h.slug === t.slug) : null;
          const image = hotelMatch?.image || t.fallbackImage;
          const tagText = t.tag ? pick(t.tag, lang) : null;
          return (
            <li
              key={`timeline-card-${t.year}-${t.title}`}
              data-timeline-card
              className="snap-start shrink-0 w-[74vw] sm:w-[46vw] md:w-[34vw] lg:w-[22rem] xl:w-[20rem]"
              data-testid={`${testIdPrefix}-card-${i}`}
              // Stable per-milestone scope so every timeline card's photo
              // maps to a deterministic slot id in /admin/images. The
              // scope combines the year with the slugified title so
              // reordering the TIMELINE array never breaks existing
              // edits.
              data-cms-scope={`heritage-timeline.${t.year}-${(t.slug || t.title || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              <article className="group h-full flex flex-col bg-[#FDFBF7] border border-[#E8DFCF] hover:border-[#C16542]/60 shadow-[0_10px_24px_-14px_rgba(44,38,33,0.18)] hover:shadow-[0_18px_36px_-14px_rgba(44,38,33,0.25)] transition-[border-color,box-shadow] duration-300 overflow-hidden">
                {/* Photo */}
                {image ? (
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#F2EBE1]">
                    <img
                      src={image}
                      alt={t.title}
                      loading="lazy"
                      decoding="async"
                      data-cms-role="card.image"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/40 via-transparent to-transparent" />
                    {/* Year overlay chip */}
                    <span className="absolute top-3 left-3 inline-flex items-center bg-[#FDFBF7]/95 text-[#C16542] font-serif-x text-lg leading-none px-2.5 py-1.5">
                      {t.year}
                    </span>
                    {tagText && (
                      <span className="absolute top-3 right-3 inline-flex items-center text-[9px] tracking-[0.22em] uppercase px-2 py-1 rounded-full bg-[#1A1513]/80 text-[#FDFBF7]">
                        {tagText}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="p-5 pb-2">
                    <span className="font-serif-x text-2xl md:text-3xl text-[#C16542] leading-none">
                      {t.year}
                    </span>
                  </div>
                )}
                {/* Body */}
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-serif-x text-lg md:text-xl leading-snug tracking-tight text-[#2C2621]">
                    {t.title}
                  </h3>
                  {t.location && (
                    <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#5C5248] flex items-center gap-1.5">
                      <MapPin className="w-3 h-3 text-[#C16542]" strokeWidth={1.6} />
                      {t.location}
                    </p>
                  )}
                  <p className="mt-3 text-[13px] leading-relaxed text-[#3C342D]/85 line-clamp-4 flex-1">
                    {pick(t.text, lang)}
                  </p>
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default HeritageTimelineCarousel;
